import { useCallback, useEffect, useMemo, useRef } from "react";
import type { PreOrder, PreOrderFlow } from "@/app/api/types/preOrder";
import { usePreOrderModifyBatch } from "@stockline/web";
import {
  type ProductDefaults,
  computeDefaultFlowWeight,
} from "../utils/resolveProductDefaults";
import { buildModifyBatchInput, NEW_FLOW_MARKER } from "../utils/modifyBatch";
import {
  buildSanitizeOps,
  mergeWithSanitizeOps,
} from "../utils/sanitizeDisabledColumns";
import { useDisabledColumnsConfig } from "./useDisabledColumnsConfig";
import {
  aiOrderKind,
  collectAiPaths,
  hasAnyPendingAi,
} from "../services/recapService";
import { resolveAiLocally } from "../services/aiResolveLocally/index";
import { useRecapAiSuggestions } from "./useRecapAiSuggestions";
import { useUserOptional } from "@/components/auth-provider";
import { track } from "@/lib/analytics";
import {
  useModifyBufferSlice,
  useModifyBufferStore,
  type ModifyAction,
} from "../stores/modifyBufferStore";
import { useRecapModeOptional } from "../contexts/RecapContext";

type EditableFlowField = Exclude<keyof PreOrderFlow, "id" | "pre_order_id">;

export interface PendingInfo {
  editedIds: Set<string>;
  addedIds: Set<string>;
  deletedIds: Set<string>;
  total: number;
}

export interface ModifyBufferApi {
  isModifying: boolean;
  isFlushing: boolean;
  lastError: { message: string; at: number; action: ModifyAction } | null;
  pendingInfo: PendingInfo;
  /** Flows to render: originals with edits merged, plus added rows, minus deleted ones. */
  bufferedFlows: PreOrderFlow[];
  isCellEdited: (flowId: string, field: EditableFlowField) => boolean;
  isFlowPendingAdd: (flowId: string) => boolean;
  isFlowPendingDelete: (flowId: string) => boolean;
  enterModify: () => void;
  cancelModify: () => void;
  /** Persist buffered changes through the V2 push-batch endpoint. The
   * server resolves any pending `ai_suggestions[]` blocks in the same
   * transaction (user edits win on field-level conflict) and decides
   * whether to trigger the ERP re-export pipeline from the post-batch
   * `pre_orders.status` — caller doesn't pick a flush flavour.
   *
   * `background` overrides the default push mode. By default a PENDING /
   * AI_PROPOSAL save pushes in the background (instant spinner clear); pass
   * `{ background: false }` when a follow-up action (e.g. confirm/export in
   * `acceptAndConfirm`) must observe the server-committed save first —
   * otherwise the awaited call returns after only the optimistic apply and the
   * two POSTs race. */
  flushSave: (options?: { background?: boolean }) => Promise<void>;
  handleFlowUpdate: (flowId: string, updates: Partial<PreOrderFlow>) => void;
  handleFlowCreate: (defaults: ProductDefaults) => void;
  handleFlowDelete: (flowId: string) => void;
  /**
   * Create a flow without going through the live fallback path: if not
   * already in modify mode, request entry first (subject to cross-PO
   * conflict gating) and stage the new row in the buffer. Used by surfaces
   * that explicitly want a confirmed/eligible PO to enter modify mode on
   * add (e.g. the partner-history "+" button), instead of either silently
   * persisting via the legacy live POST or being hidden because the PO is
   * CONFIRMED. Locked statuses are filtered at the call site.
   */
  createFlowFromHistory: (defaults: ProductDefaults) => void;
}

interface Options {
  preOrder: PreOrder;
  /**
   * Live flows for this pre-order, read by the caller from a per-PO Dexie
   * subscription (`useLiveFlowsForPreOrder`). Replaces the now-decoupled
   * `preOrder.flows` array that used to come baked into the central recap
   * pipeline — saves now propagate to the table through this subscription
   * in ~10 ms instead of waiting on the ~1 s central re-fetch.
   */
  liveFlows: PreOrderFlow[];
  /** Fallback CRUD handlers when not in modify mode (pre-existing path). */
  fallback: {
    handleFlowUpdate: (flowId: string, updates: Partial<PreOrderFlow>) => void;
    handleFlowCreate: (defaults: ProductDefaults) => void;
    handleFlowDelete: (flowId: string) => void;
  };
  /** When true, the first cell edit/create/delete optimistically enters
   * modify mode and writes directly to the buffer (skipping the fallback
   * live-write path). Used for PENDING non-proposal pre-orders so users
   * never see "blocked" cells. */
  autoEnterOnEdit?: boolean;
}

/**
 * Unwrap a clean error message from a failed flush.
 *
 * Push-batch surfaces a plain `Error` whose `message` is usually the response
 * body. The backend's 422 payload is `{ detail: { message, code } }`, which
 * may be JSON-stringified into `err.message`. Try to parse it and return only
 * the human-readable `message`; fall back to the raw string otherwise.
 */
export function extractErpErrorMessage(err: unknown): string {
  const raw =
    err instanceof Error ? err.message : "Unknown error during modify";
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      const obj = parsed as {
        detail?: { message?: unknown };
        message?: unknown;
      };
      if (typeof obj.detail?.message === "string") return obj.detail.message;
      if (typeof obj.message === "string") return obj.message;
    }
  } catch {
    // Not JSON — keep raw message
  }
  return raw;
}

/**
 * Resolve after the next two animation frames (a microtask drain + a paint),
 * or after `fallbackMs` if the tab is backgrounded and rAF throttles to ~1fps.
 * Hoisted to module scope so its nested rAF callbacks don't deepen the caller.
 */
function afterTwoFramesOrTimeout(fallbackMs: number): Promise<void> {
  return new Promise<void>((resolve) => {
    const fallback = setTimeout(resolve, fallbackMs);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        clearTimeout(fallback);
        resolve();
      }),
    );
  });
}

/**
 * Per-pre-order modify-mode buffer.
 *
 * Buffer state lives in a recap-wide store keyed by pre-order id (see
 * `stores/modifyBufferStore.ts`), so edits survive section unmount — the
 * user can collapse a pre-order row mid-edit and reopen it without losing
 * their changes. While `isModifying` is true, all cell edits/creates/deletes
 * are written to the store instead of hitting the server. When the user
 * clicks Save, the whole batch is flushed via the offline-first
 * `usePreOrderModifyBatch` (atomic V2 push-batch endpoint with optimistic
 * Dexie writes + rollback). On failure the buffer is preserved so the user
 * can retry.
 */
export function useModifyBuffer({
  preOrder,
  liveFlows,
  fallback,
  autoEnterOnEdit = false,
}: Options): ModifyBufferApi {
  const store = useModifyBufferStore();
  const slice = useModifyBufferSlice(preOrder.id);
  const preOrderId = preOrder.id;

  const modifyBatch = usePreOrderModifyBatch();
  const disabledColumns = useDisabledColumnsConfig();
  // `useUserOptional` over `useUser` so the hook is renderable in tests
  // and any unauth path; the `resolvedBy` on the AI block falls back to
  // an empty string in those edge cases, which is fine since the
  // resolver only consumes the value for audit metadata.
  const user = useUserOptional();
  // Workspace mode tags the edit events so sales vs purchases vs transport are
  // distinguishable in PostHog (all three share this same recap shell). Optional
  // read: the hook is renderable outside a RecapProvider (tests, reuse paths).
  const recapMode = useRecapModeOptional() ?? undefined;
  // Typed AI suggestion rows: `rowIndex` tells the resolver which sidecar rows
  // back each pending block; `stampResolutions` flips their lifecycle stamps
  // after the push-batch lands. Empty when no rows exist (legacy / tests).
  const { rowIndex: aiRowIndex, stampResolutions } = useRecapAiSuggestions();
  const isFlushing = modifyBatch.isPending;
  // Tracks the deferred-clear window between `pushBatch` resolving and
  // `clearBuffer` actually running (the double-rAF + 200ms fallback below).
  // `isFlushing` flips false the moment the network resolves, but the
  // buffer still holds committed edits for up to ~200ms — without this
  // ref, a concurrent `flushSave` (blur-triggered auto-save, double Save
  // click) would slip past the `isFlushing` guard and re-push the same
  // batch.
  const isClearingRef = useRef(false);

  const isModifying = slice.isModifying;

  const enterModify = useCallback(() => {
    store.requestEnterModify(preOrderId, () => {
      store.enterModify(preOrderId);
    });
  }, [store, preOrderId]);

  const cancelModify = useCallback(() => {
    store.cancelModify(preOrderId);
  }, [store, preOrderId]);

  // Auto-exit modify mode whenever the pre-order's status changes away from
  // its starting point. Covers:
  //  - PENDING → CONFIRMED (auto-modify-mode confirm flow): drain mode after
  //    flushAndConfirm so the row doesn't stay editable post-confirmation.
  //  - PENDING/CONFIRMED → cancelled/exported/etc.: clear stale buffer state
  //    so amber/red markers and controls don't linger on a non-eligible PO.
  // The buffer is already flushed/drained by the caller before the status
  // flip lands here (per useFlushPendingBuffers), so forceExitModify is safe.
  const prevStatusRef = useRef(preOrder.status);
  useEffect(() => {
    if (prevStatusRef.current !== preOrder.status && isModifying) {
      store.forceExitModify(preOrderId);
    }
    prevStatusRef.current = preOrder.status;
  }, [preOrder.status, isModifying, store, preOrderId]);

  const handleFlowUpdate = useCallback(
    (flowId: string, updates: Partial<PreOrderFlow>) => {
      if (!isModifying) {
        if (autoEnterOnEdit) {
          store.requestEnterModify(preOrderId, () => {
            // Guard: skip reset when already modifying (synchronous batch
            // calls where React state is stale but the store transitioned).
            if (!store.getSlice(preOrderId).isModifying) {
              store.enterModify(preOrderId);
            }
            store.patchOriginalFlow(preOrderId, flowId, updates);
          });
          return;
        }
        fallback.handleFlowUpdate(flowId, updates);
        return;
      }
      const isAdded = store
        .getSlice(preOrderId)
        .added.some((f) => f.id === flowId);
      if (isAdded) {
        store.patchAddedFlow(preOrderId, flowId, updates);
      } else {
        store.patchOriginalFlow(preOrderId, flowId, updates);
      }
    },
    [autoEnterOnEdit, fallback, isModifying, store, preOrderId],
  );

  const performFlowCreate = useCallback(
    (defaults: ProductDefaults) => {
      const id = crypto.randomUUID();
      const addedSlice = store.getSlice(preOrderId).added;
      const maxKnownMs = Math.max(
        Date.now() - 1,
        ...liveFlows
          .map((f) => new Date(f.created_at).getTime())
          .filter(Number.isFinite),
        ...addedSlice
          .map((f) => new Date(f.created_at).getTime())
          .filter(Number.isFinite),
      );
      const now = new Date(maxKnownMs + 1).toISOString();
      // Stamp `flowIndex = max(known)+1` so the new row's ordering doesn't
      // depend on the createdAt tiebreaker once it lands in Dexie. The recap's
      // `sortOfflineFlows` comparator is `(flowIndex ?? Infinity, createdAt)`
      // — populating `index` here pins the row to insertion order regardless
      // of the post-save tie at `transaction_timestamp()`. Floor at -1 so the
      // first row (no signal) starts at 0, matching the server's contiguous
      // 0..N-1 renumbering in batch-merge.
      const maxKnownIndex = Math.max(
        -1,
        ...liveFlows
          .map((f) => f.index)
          .filter(
            (i): i is number => typeof i === "number" && Number.isFinite(i),
          ),
        ...addedSlice
          .map((f) => f.index)
          .filter(
            (i): i is number => typeof i === "number" && Number.isFinite(i),
          ),
      );
      const nextIndex = maxKnownIndex + 1;
      const qty = defaults.quantity ?? 0;
      const weight =
        defaults.weight ??
        (qty > 0
          ? computeDefaultFlowWeight(defaults.product, defaults.unit, qty)
          : null);
      const optimisticFlow: PreOrderFlow = {
        id,
        pre_order_id: preOrderId,
        product_id: defaults.product_id,
        product: defaults.product,
        partner_id: defaults.partner_id ?? null,
        quantity: qty,
        unit_id: defaults.unit_id,
        unit: defaults.unit,
        weight,
        price_per_weight: defaults.price_per_weight ?? null,
        comment: null,
        hidden_comment: null,
        status: 0,
        index: nextIndex,
        delivery_date: null,
        order_date: null,
        flow_metadata: {
          [NEW_FLOW_MARKER]: true,
          ...(defaults.custom_partner
            ? { custom_partner: defaults.custom_partner }
            : {}),
          ...defaults.flow_metadata,
        },
        created_at: now,
        updated_at: now,
      } as PreOrderFlow;
      store.addFlow(preOrderId, optimisticFlow);
    },
    [liveFlows, store, preOrderId],
  );

  const handleFlowCreate = useCallback(
    (defaults: ProductDefaults) => {
      if (!isModifying) {
        if (autoEnterOnEdit) {
          store.requestEnterModify(preOrderId, () => {
            if (!store.getSlice(preOrderId).isModifying) {
              store.enterModify(preOrderId);
            }
            performFlowCreate(defaults);
          });
          return;
        }
        fallback.handleFlowCreate(defaults);
        return;
      }
      performFlowCreate(defaults);
    },
    [
      autoEnterOnEdit,
      fallback,
      isModifying,
      performFlowCreate,
      store,
      preOrderId,
    ],
  );

  const createFlowFromHistory = useCallback(
    (defaults: ProductDefaults) => {
      if (isModifying) {
        performFlowCreate(defaults);
        return;
      }
      store.requestEnterModify(preOrderId, () => {
        store.enterModify(preOrderId);
        performFlowCreate(defaults);
      });
    },
    [isModifying, performFlowCreate, store, preOrderId],
  );

  const handleFlowDelete = useCallback(
    (flowId: string) => {
      if (!isModifying) {
        if (autoEnterOnEdit) {
          store.requestEnterModify(preOrderId, () => {
            // Guard: skip reset when already modifying (synchronous batch
            // calls where React state is stale but the store transitioned).
            if (!store.getSlice(preOrderId).isModifying) {
              store.enterModify(preOrderId);
            }
            store.markDeleted(preOrderId, flowId);
          });
          return;
        }
        fallback.handleFlowDelete(flowId);
        return;
      }
      const isAdded = store
        .getSlice(preOrderId)
        .added.some((f) => f.id === flowId);
      if (isAdded) {
        store.removeAddedFlow(preOrderId, flowId);
      } else {
        store.markDeleted(preOrderId, flowId);
      }
    },
    [autoEnterOnEdit, fallback, isModifying, store, preOrderId],
  );

  const pendingInfo = useMemo<PendingInfo>(() => {
    const edited = new Set(Object.keys(slice.edits));
    const added = new Set(slice.added.map((f) => f.id));
    const deleted = new Set(slice.deleted);
    return {
      editedIds: edited,
      addedIds: added,
      deletedIds: deleted,
      total: edited.size + added.size + deleted.size,
    };
  }, [slice.edits, slice.added, slice.deleted]);

  const bufferedFlows = useMemo<PreOrderFlow[]>(() => {
    const base = liveFlows.filter((f) => !slice.deleted.has(f.id));
    const merged = base.map((f) => {
      const patch = slice.edits[f.id];
      return patch ? ({ ...f, ...patch } as PreOrderFlow) : f;
    });
    // Drop added flows already reconciled into `liveFlows`. pushBatch's
    // server response lands the new flow in Dexie before clearBuffer runs,
    // so the new flow can appear in `liveFlows` (and hence `base`) while
    // still in `slice.added` — without this filter, the same id renders
    // twice (duplicate-key warning + visible double row during save).
    const baseIds = new Set(base.map((f) => f.id));
    const adds = slice.added.filter((f) => !baseIds.has(f.id));
    return [...merged, ...adds];
  }, [liveFlows, slice.edits, slice.added, slice.deleted]);

  const isCellEdited = useCallback<ModifyBufferApi["isCellEdited"]>(
    (flowId, field) => {
      const patch = slice.edits[flowId];
      if (!patch) return false;
      return Object.hasOwn(patch, field);
    },
    [slice.edits],
  );
  const isFlowPendingAdd = useCallback(
    (flowId: string) => pendingInfo.addedIds.has(flowId),
    [pendingInfo],
  );
  const isFlowPendingDelete = useCallback(
    (flowId: string) => pendingInfo.deletedIds.has(flowId),
    [pendingInfo],
  );

  const buildBatchInput = useCallback(
    () => buildModifyBatchInput(preOrderId, store.getSlice(preOrderId)),
    [store, preOrderId],
  );

  /**
   * Persist buffered changes through V2 push-batch. ONE backend call:
   *
   *  - User edits (flow CRUD + header patch) land as today.
   *  - Pending `ai_suggestions[]` blocks on this pre-order are folded
   *    into the same transaction by the server, with field-level user-
   *    edit priority. Frontend stops shipping a separate accept payload —
   *    save IS the accept gesture.
   *  - The server reads the post-batch `pre_orders.status` and triggers
   *    the ERP re-export pipeline when the row is already at CONFIRMED
   *    (1) / EXPORTED (2). PENDING (0) / AI_PROPOSAL (8) saves stay
   *    local. No `intent` flag is sent — the row's state is the truth.
   *
   * Silent codes (`export_in_progress` / `export_already_succeeded` /
   * `already_in_flight`) come from the partner-group Confirm gesture's
   * single-flight guard, NOT this flush — but if a stray one surfaces,
   * keep the same swallow contract so the section doesn't paint red for
   * transient states.
   */
  const flushSave = useCallback(
    async (options?: { background?: boolean }) => {
      if (!isModifying) return;
      // Single-flight guard. Two rapid Save clicks otherwise queue two
      // concurrent push-batches against the same PO; the server lock
      // serializes them, but the second one's payload is already stale and
      // produces a confusing partial-merge. We also block during the
      // deferred buffer-clear window (rAF/200ms fallback) — `isFlushing`
      // flips false as soon as the network resolves, but the buffer is
      // not yet cleared, so a concurrent flushSave would re-push the same
      // batch.
      if (isFlushing || isClearingRef.current) return;
      const fresh = store.getSlice(preOrderId);
      const hasUserWork =
        Object.keys(fresh.edits).length > 0 ||
        fresh.added.length > 0 ||
        fresh.deleted.size > 0;
      const hasAi = hasAnyPendingAi(preOrder);
      // Skip when there's no work AND no AI to resolve AND status isn't
      // CONFIRMED (where Save is the re-export trigger).
      if (!hasUserWork && preOrder.status !== 1 && !hasAi) {
        return;
      }
      // Resolve AI client-side — the resolver produces the merged
      // ModifyBatchInput (column writes + flipped `ai_suggestions[]`
      // blocks + parent status transition + wholesale-decline soft-
      // delete). Backend just applies the payload. One push, one truth.
      const base = buildBatchInput();
      const resolved = hasAi
        ? resolveAiLocally(
            {
              userId: user?.id ?? "",
              resolvedAt: new Date().toISOString(),
            },
            preOrder,
            base,
            "accept",
            aiRowIndex,
          )
        : { batch: base, parentSoftDeleted: false, resolutions: [] };

      // Nullify values of feature-flag-disabled columns so invisible data
      // never persists or leaks to ERPs. Merges into the existing ops so
      // flows already in the batch get the sanitization inlined.
      const sanitizeOps = buildSanitizeOps(liveFlows, disabledColumns);
      resolved.batch = {
        ...resolved.batch,
        flowChanges: mergeWithSanitizeOps(
          resolved.batch.flowChanges,
          sanitizeOps,
        ),
      };
      const recordFlushError = (err: unknown) => {
        const code =
          err && typeof err === "object" && "code" in err
            ? (err as { code: unknown }).code
            : undefined;
        if (
          code === "export_in_progress" ||
          code === "export_already_succeeded" ||
          code === "already_in_flight"
        ) {
          return;
        }
        store.setLastError(preOrderId, {
          message: extractErpErrorMessage(err),
          at: Date.now(),
          action: "flushSave",
        });
      };
      // A CONFIRMED / EXPORTED parent re-exports to M3 on save — keep the spinner
      // until the server responds (its "Confirming…" state is the loading state).
      // A PENDING / AI_PROPOSAL save never touches the ERP, so push in the
      // BACKGROUND: the spinner clears the moment the optimistic apply lands
      // (instant, like delete), and a failure reverts + records the error.
      // status 1 = CONFIRMED, 2 = EXPORTED (numeric to match the `!== 1` check above).
      const isReexport = preOrder.status === 1 || preOrder.status === 2;
      // A re-export always blocks on the server; otherwise honor the caller's
      // override (acceptAndConfirm forces foreground so its confirm POST runs
      // strictly after the server-committed save), defaulting to background.
      const background = isReexport ? false : (options?.background ?? true);
      // Defer the buffer clear until Dexie's useLiveQuery has propagated the
      // post-save state. Without this, isModifying flips false and the affected
      // rows render the pre-save liveFlows for one frame before the
      // subscription fires — a visible "revert and re-apply" flicker. Two rAFs
      // span a microtask drain + paint; a 200ms timeout falls back in case the
      // tab is backgrounded (rAF throttles to ~1fps).
      const deferredClearBuffer = async (): Promise<void> => {
        await afterTwoFramesOrTimeout(200);
        store.clearBuffer(preOrderId);
        isClearingRef.current = false;
      };
      try {
        isClearingRef.current = true;
        await modifyBatch.pushBatch(resolved.batch, {
          background,
          // Product analytics fire from onSuccess — which runs after the POST +
          // reconcile in BOTH await and background modes — so a background save
          // whose server write later reverts never inflates the counts. Events
          // derive from the user's buffered work (`fresh`), not the AI-merged
          // batch; `field` is the column name only, never the value.
          onSuccess: () => {
            fresh.added.forEach(() =>
              track("pre_order_line_added", {
                pre_order_id: preOrderId,
                surface: "recap_modify",
                mode: recapMode,
              }),
            );
            fresh.deleted.forEach(() =>
              track("pre_order_line_removed", {
                pre_order_id: preOrderId,
                surface: "recap_modify",
                mode: recapMode,
              }),
            );
            for (const [flowId, patch] of Object.entries(fresh.edits)) {
              for (const field of Object.keys(patch)) {
                track("pre_order_line_updated", {
                  pre_order_id: preOrderId,
                  field,
                  surface: "recap_modify",
                  mode: recapMode,
                });
              }
              // status 1 = Confirmed (StatusCell click-cycle). The buffer only
              // holds changed fields, so a status in the patch IS a toggle.
              if (patch.status === 1) {
                track("pre_order_flow_confirmed", {
                  flow_id: flowId,
                  pre_order_id: preOrderId,
                });
              }
            }
            // A modify-mode Save that resolves pending AI (Save, Accept AI,
            // Save & Confirm all flow through here) IS an AI accept — emit it
            // so those accepts aren't blind to the funnel. `flow_count`
            // excludes the "header" path, matching the other accept surfaces.
            if (hasAi) {
              track("ai_proposal_accepted", {
                pre_order_id: preOrderId,
                scope: "all",
                flow_count: collectAiPaths(preOrder).filter((p) =>
                  p.startsWith("flows."),
                ).length,
                mode: recapMode,
                order_kind: aiOrderKind(preOrder),
              });
              // Stamp the sidecar suggestion rows accepted/declined — the
              // durable lifecycle lives on the row, not the (now-stripped)
              // metadata block. Fire-and-forget through the offline-first
              // resolve mutations; failure is non-fatal (the next sync
              // reconciles, and the column writes already committed).
              void stampResolutions(resolved.resolutions);
            }
            // A background save returns optimistically BEFORE its POST
            // resolves, so the buffer can only be cleared here, on confirmed
            // success. Clearing it unconditionally after the optimistic return
            // would wipe the buffer AND any `lastError` a later `onError` sets
            // — a failed background save could then never surface (no failed
            // badge, no retry, no buffer to re-push). The foreground path
            // clears below: reaching past the awaited push means success, since
            // a failure threw into the catch.
            if (background) void deferredClearBuffer();
          },
          // Background path surfaces POST failures via onError (it doesn't
          // throw): record the error and KEEP the buffer so the failed badge
          // shows and the user can retry. The awaited/foreground path lets the
          // catch below handle them, so don't double-report.
          ...(background
            ? {
                onError: (err: unknown) => {
                  isClearingRef.current = false;
                  // If the user cancelled modify mode while this background
                  // POST was in flight, its edits were already discarded —
                  // stamping lastError on the now-empty buffer would surface a
                  // phantom failed badge on the next modify entry. Only record
                  // the failure if the buffer is still active.
                  if (!store.getSlice(preOrderId).isModifying) return;
                  recordFlushError(err);
                },
              }
            : {}),
        });
        // Foreground (re-export) only — a background save's buffer is cleared
        // in `onSuccess` (or kept with `lastError` on failure), and its POST is
        // still in flight here.
        if (!background) await deferredClearBuffer();
      } catch (err) {
        isClearingRef.current = false;
        recordFlushError(err);
      }
    },
    [
      aiRowIndex,
      buildBatchInput,
      disabledColumns,
      isFlushing,
      isModifying,
      liveFlows,
      modifyBatch,
      preOrder,
      preOrderId,
      recapMode,
      stampResolutions,
      store,
      user,
    ],
  );

  return {
    isModifying,
    isFlushing,
    lastError: slice.lastError,
    pendingInfo,
    bufferedFlows,
    isCellEdited,
    isFlowPendingAdd,
    isFlowPendingDelete,
    enterModify,
    cancelModify,
    flushSave,
    handleFlowUpdate,
    handleFlowCreate,
    handleFlowDelete,
    createFlowFromHistory,
  };
}
